﻿// ── Per-Tab Sidebar Configuration ──
// Used by page.tsx to render the correct sidebar for each top-level tab

export const TAB_KEYS = ['introduction', 'video', 'application', 'certification', 'report', 'materials'] as const;
export type TabKey = (typeof TAB_KEYS)[number];

export const TAB_LABELS: Record<TabKey, string> = {
  video: '成果视频',
  introduction: '成果简介',
  application: '申报书',
  certification: '鉴定证书',
  report: '成果报告',
  materials: '支撑材料',
};

export const TAB_ICONS: Record<TabKey, string> = {
  video: 'ri-video-line',
  introduction: 'ri-file-text-line',
  application: 'ri-article-line',
  certification: 'ri-shield-check-line',
  report: 'ri-bar-chart-box-line',
  materials: 'ri-folder-2-line',
};

export interface TabSidebarConfig {
  sidebarItems: SidebarNavItem[];
  defaultSectionId?: string;
}

// ═══════ TAB SIDEBAR MAP ═══════
export const tabSidebarMap: Record<TabKey, TabSidebarConfig> = {
  // ── 成果简介 ──
  introduction: {
    sidebarItems: [
      {
        id: 'intro-group',
        title: '成果简介',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'intro-main', label: '成果简介', icon: 'ri-file-text-line' },
        ],
      },
    ],
    defaultSectionId: 'intro-main',
  },

  // ── 成果视频: single video ──
  video: {
    sidebarItems: [
      {
        id: 'vid-group',
        title: '成果视频',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'vid-overview', label: '成果总览视频', icon: 'ri-film-line' },
        ],
      },
    ],
    defaultSectionId: 'vid-overview',
  },

  // ── 申报书: group + 2 singles ──
  application: {
    sidebarItems: [
      {
        id: 'app-form-group',
        title: '申报书',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'app-form', label: '申报书预览', icon: 'ri-file-list-3-line' },
          { id: 'app-awards', label: '成果曾获奖励', icon: 'ri-medal-line' },
          { id: 'app-timeline', label: '成果起止时间', icon: 'ri-calendar-line' },
        ],
      },
      { id: 'section-contributors', title: '主要完成人情况', type: 'single', icon: 'ri-user-settings-line' },
      { id: 'section-units', title: '主要完成单位情况', type: 'single', icon: 'ri-building-line' },
    ],
    defaultSectionId: 'app-form',
  },

  // ── 鉴定证书 ──
  certification: {
    sidebarItems: [
      {
        id: 'cert-group',
        title: '鉴定证书',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'cert-assoc', label: '协会鉴定', icon: 'ri-shield-check-line' },
          { id: 'cert-edu', label: '教育厅鉴定', icon: 'ri-government-line' },
        ],
      },
    ],
    defaultSectionId: 'cert-assoc',
  },

  // ── 支撑材料: 1 big group with 15 items ──
  materials: {
    sidebarItems: [
      {
        id: 'mat-group',
        title: '支撑材料',
        type: 'group',
        defaultExpanded: true,
        items: [
                  { id: 'mat-honors', label: '专业所获荣誉', icon: 'ri-award-line' },
          { id: 'mat-curriculum', label: '课程建设', icon: 'ri-book-open-line' },
          { id: 'mat-textbooks', label: '教材建设', icon: 'ri-stack-line' },
          { id: 'mat-platform', label: '平台建设', icon: 'ri-server-line' },
          { id: 'mat-faculty', label: '师资队伍建设', icon: 'ri-team-line' },
          { id: 'mat-party', label: '党建思政建设', icon: 'ri-flag-line' },
          { id: 'mat-reform-projects', label: '代表性教改研究项目', icon: 'ri-rocket-line' },
          { id: 'mat-industry-cooperation', label: '产教合作基地建设', icon: 'ri-exchange-line' },
          { id: 'mat-international', label: '国际化及卓越人才培养', icon: 'ri-earth-line' },
          { id: 'mat-student-awards', label: '学生学科竞赛获奖与创新创业项目', icon: 'ri-trophy-line' },
          { id: 'mat-outstanding-graduates', label: '优秀毕业生典型', icon: 'ri-star-line' },
          { id: 'mat-enterprise-evaluation', label: '企业评价', icon: 'ri-feedback-line' },
          { id: 'mat-social-impact', label: '社会影响', icon: 'ri-newspaper-line' },
          { id: 'mat-application', label: '成果应用', icon: 'ri-share-forward-line' },
        ],
      },
    ],
    defaultSectionId: 'mat-honors',
  },

  // ── 成果报告: group with 4 items ──
  report: {
    sidebarItems: [
      {
        id: 'rep-group',
        title: '成果报告',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'rep-background', label: '成果研究背景', icon: 'ri-history-line' },
          { id: 'rep-approach', label: '成果主要做法', icon: 'ri-route-line' },
          { id: 'rep-innovation', label: '成果主要创新', icon: 'ri-flashlight-line' },
          { id: 'rep-promotion', label: '成果应用推广', icon: 'ri-global-line' },
        ],
      },
    ],
    defaultSectionId: 'rep-background',
  },
};

export interface SidebarNavGroup {
  id: string;
  title: string;
  type: 'group';
  defaultExpanded?: boolean;
  items: { id: string; label: string; icon?: string }[];
}

export interface SidebarNavSingle {
  id: string;
  title: string;
  type: 'single';
  icon?: string;
}

export type SidebarNavItem = SidebarNavGroup | SidebarNavSingle;

export interface FileItem {
  name: string;
  fileType: 'pdf' | 'image' | 'video' | 'file';
  url: string;
}

export interface TextContentData {
  contentType: 'text';
  title: string;
  content: string;
}

export interface FilesContentData {
  contentType: 'files';
  title: string;
  files: FileItem[];
}

export type SectionContent = TextContentData | FilesContentData;

// ── Sidebar Navigation Structure (legacy) ──
export const sidebarNav: SidebarNavItem[] = [
  {
    id: 'supporting-materials',
    title: '支撑材料',
    type: 'group',
    defaultExpanded: true,
    items: [
          { id: 'mat-honors', label: '专业所获荣誉', icon: 'ri-award-line' },
      { id: 'mat-curriculum', label: '课程建设', icon: 'ri-book-open-line' },
      { id: 'mat-textbooks', label: '教材建设', icon: 'ri-stack-line' },
      { id: 'mat-platform', label: '平台建设', icon: 'ri-server-line' },
      { id: 'mat-faculty', label: '师资队伍建设', icon: 'ri-team-line' },
      { id: 'mat-party', label: '党建思政建设', icon: 'ri-flag-line' },
      { id: 'mat-reform-projects', label: '代表性教改研究项目', icon: 'ri-rocket-line' },
      { id: 'mat-industry-cooperation', label: '产教合作基地建设', icon: 'ri-exchange-line' },
      { id: 'mat-international', label: '国际化及卓越人才培养', icon: 'ri-earth-line' },
      { id: 'mat-student-awards', label: '学生学科竞赛获奖与创新创业项目', icon: 'ri-trophy-line' },
      { id: 'mat-outstanding-graduates', label: '优秀毕业生典型', icon: 'ri-star-line' },
      { id: 'mat-enterprise-evaluation', label: '企业评价', icon: 'ri-feedback-line' },
      { id: 'mat-social-impact', label: '社会影响', icon: 'ri-newspaper-line' },
      { id: 'mat-application', label: '成果应用', icon: 'ri-share-forward-line' },
    ],
  },
  {
    id: 'application-form',
    title: '申报书',
    type: 'group',
    items: [
      { id: 'app-form', label: '申报书预览', icon: 'ri-file-list-3-line' },
      { id: 'app-awards', label: '成果曾获奖励', icon: 'ri-medal-line' },
      { id: 'app-timeline', label: '成果起止时间', icon: 'ri-calendar-line' },
    ],
  },
  {
    id: 'section-intro',
    title: '成果简介及主要解决的教学问题',
    type: 'single',
    icon: 'ri-information-line',
  },
  {
    id: 'section-methods',
    title: '成果解决教学问题的方法',
    type: 'single',
    icon: 'ri-tools-line',
  },
  {
    id: 'section-innovation',
    title: '成果的创新点',
    type: 'single',
    icon: 'ri-sparkling-line',
  },
  {
    id: 'section-promotion',
    title: '成果的推广应用效果',
    type: 'single',
    icon: 'ri-share-circle-line',
  },
  {
    id: 'section-contributors',
    title: '主要完成人情况',
    type: 'single',
    icon: 'ri-user-settings-line',
  },
  {
    id: 'section-units',
    title: '主要完成单位情况',
    type: 'single',
    icon: 'ri-building-line',
  },
  {
    id: 'report',
    title: '成果报告',
    type: 'group',
    items: [
      { id: 'rep-background', label: '成果研究背景', icon: 'ri-history-line' },
      { id: 'rep-approach', label: '成果主要做法', icon: 'ri-route-line' },
      { id: 'rep-innovation', label: '成果主要创新', icon: 'ri-flashlight-line' },
      { id: 'rep-promotion', label: '成果应用推广', icon: 'ri-global-line' },
    ],
  },
];

// ── Content Map (keyed by sidebar item id) ──

const mockFiles: FileItem[] = [
  { name: '相关文件材料（一）', fileType: 'pdf', url: '/最终版.pdf' },
  { name: '佐证材料汇编', fileType: 'pdf', url: '/最终版.pdf' },
  { name: '成果证明材料扫描件', fileType: 'image', url: '#' },
  { name: '现场照片集锦', fileType: 'image', url: '#' },
  { name: '成果展示视频', fileType: 'video', url: '#' },
];

// Real PDF attachment URL
const REAL_PDF_URL = '/最终版.pdf';

export const contentMap: Record<string, SectionContent> = {
  // ── 成果简介 ──
  'intro-main': {
    contentType: 'files',
    title: '成果简介',
    files: [
      { name: '成果简介', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },

  // ── 鉴定证书 ──
  'cert-assoc': {
    contentType: 'files',
    title: '协会鉴定',
    files: [
      { name: '协会鉴定', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'cert-edu': {
    contentType: 'files',
    title: '教育厅鉴定',
    files: [
      { name: '教育厅鉴定', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },

  // ── 成果视频 ──
  'vid-overview': {
    contentType: 'files',
    title: '成果总览视频',
    files: [
      { name: '教学成果总览宣传片', fileType: 'video', url: '#' },
    ],
  },

  // ── 支撑材料 ──
  'mat-honors': {
    contentType: 'files',
    title: '专业所获荣誉',
    files: [
      { name: '国家一流本科专业建设点批文', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-curriculum': {
    contentType: 'files',
    title: '课程建设',
    files: [
      { name: '国家级一流课程认定文件', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-textbooks': {
    contentType: 'files',
    title: '教材建设',
    files: [
      { name: '《轨道交通导论》十四五规划教材封面及目录', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-platform': {
    contentType: 'files',
    title: '平台建设',
    files: [
      { name: '国家级实验教学示范中心批文', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-faculty': {
    contentType: 'files',
    title: '师资队伍建设',
    files: [
      { name: '教学团队人员构成一览表', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-party': {
    contentType: 'files',
    title: '党建思政建设',
    files: [
      { name: '全国党建工作样板支部批文', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-reform-projects': {
    contentType: 'files',
    title: '代表性教改研究项目',
    files: [
      { name: '教育部新工科研究与实践项目任务书', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-industry-cooperation': {
    contentType: 'files',
    title: '产教合作基地建设',
    files: [
      { name: '校企合作协议书汇编（12份）', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-international': {
    contentType: 'files',
    title: '国际化及卓越人才培养',
    files: [
      { name: '国际合作办学协议', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-student-awards': {
    contentType: 'files',
    title: '学生学科竞赛获奖与创新创业项目',
    files: [
      { name: '国家级学科竞赛获奖证书汇总（56项）', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-outstanding-graduates': {
    contentType: 'files',
    title: '优秀毕业生典型',
    files: [
      { name: '优秀毕业生典型案例汇编', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-enterprise-evaluation': {
    contentType: 'files',
    title: '企业评价',
    files: [
      { name: '用人单位满意度调查报告', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-social-impact': {
    contentType: 'files',
    title: '社会影响',
    files: [
      { name: '《中国教育报》专题报道', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'mat-application': {
    contentType: 'files',
    title: '成果应用',
    files: [
      { name: '兄弟院校应用证明（8所高校）', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },

  // ── 申报书 ──
  'app-form': {
    contentType: 'files',
    title: '申报书预览',
    files: [
      { name: '申报书封面及基本信息', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },
  'app-awards': {
    contentType: 'files',
    title: '成果曾获奖励',
    files: [
      { name: '国家级教学成果二等奖证书', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },
  'app-timeline': {
    contentType: 'files',
    title: '成果起止时间',
    files: [
      { name: '项目立项审批文件', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },

  // ... rest of the content remains the same
  // ── 成果简介 ──
  'section-intro': {
    contentType: 'text',
    title: '成果简介及主要解决的教学问题',
    content: `<h3>一、成果基本情况</h3>
<p>本成果是华东交通大学在轨道交通人才培养领域的一项系统性改革成果，历时六年（2020-2026），覆盖全校6个轨道交通相关专业，涉及在校学生8000余人。成果以产教融合为核心，构建了"校企协同、工学交替、项目驱动"三位一体的人才培养新模式，形成了覆盖轨道交通全产业链的校企合作网络。</p>
<p>六年来，累计培养高素质应用型人才3000余名，毕业生就业率连续四年保持在96%以上，用人单位满意度达94.7%。成果获得国家级教学成果二等奖1项、江西省教学成果特等奖1项，在全国同类高校中产生了广泛影响。</p>
<h3>二、主要解决的教学问题</h3>
<p><strong>问题一：人才培养与产业需求脱节</strong>——传统培养方案中课程内容更新滞后于行业发展，毕业生知识结构与岗位能力要求存在较大差距，企业普遍反映新员工适应周期过长。</p>
<p><strong>问题二：实践教学资源不足，能力培养弱化</strong>——校内实验实训条件有限，真实工程场景难以再现；企业参与实践教学的积极性不高，学生工程实践能力培养缺乏有效载体。</p>
<p><strong>问题三：校企合作机制不健全，合作深度不够</strong>——校企合作多停留在浅层次的实习实训层面，缺乏在培养方案制定、课程共建、师资共享等方面的深度合作，企业优质资源未能充分转化为教学资源。</p>
<p><strong>问题四：教师工程实践能力不足</strong>——青年教师大多从校门到校门，缺乏行业工程实践经历，"双师型"教师比例偏低，影响了工程教育质量。</p>`,
  },

  // ── 解决问题的方法 ──
  'section-methods': {
    contentType: 'text',
    title: '成果解决教学问题的方法',
    content: `<h3>一、构建"三段递进式"实践教学体系</h3>
<p>打破传统"先理论后实践"的线性模式，构建"基础认知→专业实训→综合创新"三段递进式实践教学体系。第一阶段（大一大二）：通过企业参观、行业讲座等方式建立专业认知；第二阶段（大二大三）：依托校企共建实训基地开展专业核心技能训练；第三阶段（大三大四）：以企业真实项目为载体，开展综合设计和创新实践。三个阶段贯穿本科四年，实现实践能力培养的全程化和递进化。</p>
<h3>二、创建"校中厂、厂中校"校企深度融合模式</h3>
<p>一方面，将企业的真实生产线、研发平台引入校园，建成校内"产业微环境"，学生在校内即可接触行业前沿技术和工作流程；另一方面，在企业设立"驻企教室"，将部分专业课程搬到企业现场进行教学，由企业工程师和学校教师共同授课，实现"做中学、学中做"的深度融合。</p>
<h3>三、建立基于行业标准的课程体系与评价机制</h3>
<p>对接轨道交通行业职业资格标准，重构专业课程体系，将行业标准、岗位能力要求融入课程目标和教学内容。建立"学校+企业+行业"三方评价机制，引入企业专家参与课程考核和毕业设计评审，确保人才培养质量与行业需求精准对接。</p>
<h3>四、形成"双师双能型"教师队伍建设长效机制</h3>
<p>建立教师企业实践锻炼制度，要求45岁以下青年教师每三年至少到企业实践锻炼6个月；聘请企业技术骨干担任兼职教师，承担实践教学任务；设立"产业教授"岗位，吸引行业高端人才深度参与人才培养全过程。目前"双师型"教师比例已达78%。</p>`,
  },

  // ── 创新点 ──
  'section-innovation': {
    contentType: 'text',
    title: '成果的创新点',
    content: `<h3>创新点一：首创"三段递进式"实践教学体系</h3>
<p>突破传统实践教学"集中安排、阶段割裂"的局限，在国内轨道交通类高校中率先构建"基础认知→专业实训→综合创新"的三段递进式实践教学体系，实现了实践教学从"蜻蜓点水"到"全程贯通"的系统性变革。该体系以能力进阶为主线，将企业资源、工程项目、创新训练有机融入各阶段，形成了一条完整的工程实践能力培养链。</p>
<h3>创新点二：构建"校中厂、厂中校"双向嵌入的产教融合新形态</h3>
<p>改变以往"学校主导、企业配合"的单向合作模式，创新性地提出了"校中厂、厂中校"的双向嵌入合作模式。在校内引入企业生产线和研发平台，在企业设立教学空间，实现了教育资源与产业资源的双向流动和深度融合。这一模式使校企从"两张皮"变成了"一条心"，真正做到了人才共育、过程共管、成果共享。</p>
<h3>创新点三：建立"三对接"课程建设机制</h3>
<p>首创课程内容与职业标准对接、教学过程与生产过程对接、毕业要求与岗位能力对接的"三对接"课程建设机制。通过企业深度参与培养方案制定、课程内容更新和考核标准设计，确保人才培养供给侧与产业需求侧精准匹配。</p>
<h3>创新点四：形成"引培并举、专兼结合"的师资建设新模式</h3>
<p>突破传统高校教师队伍建设思路，建立教师企业锻炼、企业专家进课堂、产业教授聘任三位一体的师资建设新机制，形成了一支理论功底扎实、工程经验丰富的"双师双能型"教学团队。</p>`,
  },

  // ── 推广应用效果 ──
  'section-promotion': {
    contentType: 'text',
    title: '成果的推广应用效果',
    content: `<h3>一、校内应用成效显著</h3>
<p>成果已在华东交通大学6个轨道交通类专业全面实施，覆盖本科生8000余人。实施以来，学生在各类学科竞赛中获得国家级奖项56项、省级奖项182项，数量和质量均较改革前大幅提升。毕业生就业率连续四年保持在96%以上，其中行业对口就业率达82%，用人单位满意度达94.7%。2024届毕业生中，有127人进入中国中车、中国铁建等央企工作，较改革前增长52%。</p>
<h3>二、校外推广辐射广泛</h3>
<p>成果被南昌大学、长沙理工大学、兰州交通大学、石家庄铁道大学、大连交通大学、重庆交通大学、西南科技大学、北京联合大学等8所高校借鉴应用，受益学生超过20000人。各应用高校结合自身实际进行了适应性调整，均取得了良好的应用效果。</p>
<h3>三、社会评价高度认可</h3>
<p>成果获得《中国教育报》《江西日报》等主流媒体专题报道10余次。教育部本科教学工作审核评估专家组对成果给予高度评价，认为"该成果在产教融合人才培养方面形成了可复制、可推广的经验"。2025年，成果作为典型案例在全国新建本科院校联席会议上进行交流展示。</p>
<h3>四、行业影响持续扩大</h3>
<p>成果核心内容被纳入江西省"十四五"教育事业发展规划，相关经验在中国铁道学会教育教学年会上作专题报告3次。成果团队受邀为全国12所高校提供产教融合人才培养方案咨询服务。</p>`,
  },

  // ── 主要完成人情况 ──
  'section-contributors': {
    contentType: 'files',
    title: '主要完成人情况',
    files: [
      { name: '主要完成人情况', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },

  // ── 主要完成单位情况 ──
  'section-units': {
    contentType: 'files',
    title: '主要完成单位情况',
    files: [
      { name: '主要完成单位情况', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },

  // ── 成果报告 ──
  'rep-background': {
    contentType: 'files',
    title: '成果研究背景',
    files: [
      { name: '成果研究背景', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },
  'rep-approach': {
    contentType: 'files',
    title: '成果主要做法',
    files: [
      { name: '成果主要做法', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },
  'rep-innovation': {
    contentType: 'files',
    title: '成果主要创新',
    files: [
      { name: '成果主要创新', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },
  'rep-promotion': {
    contentType: 'files',
    title: '成果应用推广',
    files: [
      { name: '成果应用推广', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },
};